import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  BrandStoreForMap,
  StateStoreCount,
  fetchStoreCountsByState,
  fetchStoresForViewport,
} from '../../services/mapPageService';

export type ViewingMode = 'all' | 'whitespace' | 'recommended';

export interface RegionFilters {
  channels: string[];
  customerType: string;
  states?: string[];
  cities?: string[];
  retailers?: string[];
  brands?: string[];
  categories?: string[];
  solutions?: string[];
  whitespace?: string[];
  viewingMode?: ViewingMode;
  penetrationMin?: number;
  penetrationMax?: number;
  crimeIndex?: string[];
  weatherHotMonthsMin?: number;
  weatherHotMonthsMax?: number;
  weatherColdMonthsMin?: number;
  weatherColdMonthsMax?: number;
  shoppingComplexOnly?: boolean;
  cartOrdinance?: string;
}

interface RegionViewState {
  stateAggregates: StateStoreCount[];
  viewportStores: BrandStoreForMap[];
  isLoading: boolean;
  currentZoomTier: 'state' | 'cluster' | 'detail';
}

const STATE_COORDS: Record<string, { lat: number; lng: number }> = {
  AL: { lat: 32.806671, lng: -86.79113 }, AK: { lat: 63.588753, lng: -154.493062 },
  AZ: { lat: 34.048928, lng: -111.093731 }, AR: { lat: 35.20105, lng: -91.831833 },
  CA: { lat: 36.778261, lng: -119.417932 }, CO: { lat: 39.550051, lng: -105.782067 },
  CT: { lat: 41.603221, lng: -73.087749 }, DE: { lat: 38.910832, lng: -75.52767 },
  FL: { lat: 27.664827, lng: -81.515754 }, GA: { lat: 32.157435, lng: -82.907123 },
  HI: { lat: 19.898682, lng: -155.665857 }, ID: { lat: 44.068202, lng: -114.742041 },
  IL: { lat: 40.633125, lng: -89.398528 }, IN: { lat: 40.551217, lng: -85.602364 },
  IA: { lat: 41.878003, lng: -93.097702 }, KS: { lat: 39.011902, lng: -98.484246 },
  KY: { lat: 37.839333, lng: -84.270018 }, LA: { lat: 31.244823, lng: -92.145024 },
  ME: { lat: 45.253783, lng: -69.445469 }, MD: { lat: 39.045755, lng: -76.641271 },
  MA: { lat: 42.407211, lng: -71.382437 }, MI: { lat: 44.314844, lng: -85.602364 },
  MN: { lat: 46.729553, lng: -94.6859 }, MS: { lat: 32.354668, lng: -89.398528 },
  MO: { lat: 37.964253, lng: -91.831833 }, MT: { lat: 46.879682, lng: -110.362566 },
  NE: { lat: 41.492537, lng: -99.901813 }, NV: { lat: 38.80261, lng: -116.419389 },
  NH: { lat: 43.193852, lng: -71.572395 }, NJ: { lat: 40.058324, lng: -74.405661 },
  NM: { lat: 34.97273, lng: -105.032363 }, NY: { lat: 43.299428, lng: -74.217933 },
  NC: { lat: 35.759573, lng: -79.0193 }, ND: { lat: 47.551493, lng: -101.002012 },
  OH: { lat: 40.417287, lng: -82.907123 }, OK: { lat: 35.007752, lng: -97.092877 },
  OR: { lat: 43.804133, lng: -120.554201 }, PA: { lat: 41.203322, lng: -77.194525 },
  RI: { lat: 41.580095, lng: -71.477429 }, SC: { lat: 33.836081, lng: -81.163725 },
  SD: { lat: 43.969515, lng: -99.901813 }, TN: { lat: 35.517491, lng: -86.580447 },
  TX: { lat: 31.968599, lng: -99.901813 }, UT: { lat: 39.32098, lng: -111.093731 },
  VT: { lat: 44.558803, lng: -72.577841 }, VA: { lat: 37.431573, lng: -78.656894 },
  WA: { lat: 47.751074, lng: -120.740139 }, WV: { lat: 38.597626, lng: -80.454903 },
  WI: { lat: 43.78444, lng: -88.787868 }, WY: { lat: 43.075968, lng: -107.290284 },
  DC: { lat: 38.907192, lng: -77.036871 }, PR: { lat: 18.220833, lng: -66.590149 },
};

export const getStateCoords = (stateCode: string) => STATE_COORDS[stateCode.toUpperCase()];

// Map full state names (lowercase keys) to 2-letter codes for case-insensitive lookup
const STATE_NAME_TO_CODE: Record<string, string> = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA',
  colorado: 'CO', connecticut: 'CT', delaware: 'DE', florida: 'FL', georgia: 'GA',
  hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA',
  kansas: 'KS', kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
  massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS', missouri: 'MO',
  montana: 'MT', nebraska: 'NE', nevada: 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
  'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND',
  ohio: 'OH', oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI',
  'south carolina': 'SC', 'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT',
  vermont: 'VT', virginia: 'VA', washington: 'WA', 'west virginia': 'WV',
  wisconsin: 'WI', wyoming: 'WY', 'district of columbia': 'DC', 'puerto rico': 'PR',
};

const getZoomTier = (zoom: number): 'state' | 'cluster' | 'detail' => {
  if (zoom <= 8) return 'state';
  if (zoom <= 12) return 'cluster';
  return 'detail';
};

const roundBounds = (val: number) => Math.round(val * 100) / 100;

export const useRegionView = (
  map: google.maps.Map | null,
  filters: RegionFilters,
  enabled: boolean
): RegionViewState => {
  const [stateAggregates, setStateAggregates] = useState<StateStoreCount[]>([]);
  const [viewportStores, setViewportStores] = useState<BrandStoreForMap[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentZoomTier, setCurrentZoomTier] = useState<'state' | 'cluster' | 'detail'>('state');
  const prevBoundsKeyRef = useRef('');
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const stateAggregatesLoadedRef = useRef(false);
  const prevFilterKeyRef = useRef('');

  // Fetch state aggregates only when needed (state zoom tier)
  const fetchStateAggregates = useCallback(async () => {
    const filterKey = [
      (filters.states || []).sort().join(','),
      (filters.cities || []).sort().join(','),
      (filters.retailers || []).sort().join(','),
      (filters.brands || []).sort().join(','),
      (filters.categories || []).sort().join(','),
      (filters.solutions || []).sort().join(','),
      (filters.whitespace || []).sort().join(','),
      filters.viewingMode || 'all',
      filters.penetrationMin ?? 0, filters.penetrationMax ?? 100,
      (filters.crimeIndex || []).sort().join(','),
      filters.weatherHotMonthsMin ?? 0, filters.weatherHotMonthsMax ?? 12,
      filters.weatherColdMonthsMin ?? 0, filters.weatherColdMonthsMax ?? 12,
      filters.shoppingComplexOnly ? '1' : '0',
      filters.cartOrdinance || 'Any',
    ].join('|');
    const alreadyLoaded = stateAggregatesLoadedRef.current && prevFilterKeyRef.current === filterKey;
    if (alreadyLoaded) return;
    stateAggregatesLoadedRef.current = true;
    prevFilterKeyRef.current = filterKey;
    setIsLoading(true);
    try {
      const stateCodes = filters.states?.map((name) => STATE_NAME_TO_CODE[name.toLowerCase()]).filter(Boolean);
      const hasFilters = (filters.states && filters.states.length > 0) || (filters.cities && filters.cities.length > 0) || (filters.retailers && filters.retailers.length > 0) || (filters.brands && filters.brands.length > 0) || (filters.categories && filters.categories.length > 0) || (filters.solutions && filters.solutions.length > 0) || (filters.whitespace && filters.whitespace.length > 0) || (filters.viewingMode && filters.viewingMode !== 'all') ||
        (filters.penetrationMin !== undefined && filters.penetrationMin > 0) || (filters.penetrationMax !== undefined && filters.penetrationMax < 100) ||
        (filters.crimeIndex && filters.crimeIndex.length > 0) ||
        (filters.weatherHotMonthsMin !== undefined && filters.weatherHotMonthsMin > 0) || (filters.weatherHotMonthsMax !== undefined && filters.weatherHotMonthsMax < 12) ||
        (filters.weatherColdMonthsMin !== undefined && filters.weatherColdMonthsMin > 0) || (filters.weatherColdMonthsMax !== undefined && filters.weatherColdMonthsMax < 12) ||
        filters.shoppingComplexOnly || (filters.cartOrdinance && filters.cartOrdinance !== 'Any');
      const data = await fetchStoreCountsByState(
        hasFilters ? {
          states: stateCodes && stateCodes.length > 0 ? stateCodes : undefined,
          cities: filters.cities && filters.cities.length > 0 ? filters.cities : undefined,
          retailers: filters.retailers && filters.retailers.length > 0 ? filters.retailers : undefined,
          brands: filters.brands && filters.brands.length > 0 ? filters.brands : undefined,
          categories: filters.categories && filters.categories.length > 0 ? filters.categories : undefined,
          solutions: filters.solutions && filters.solutions.length > 0 ? filters.solutions : undefined,
          whitespace: filters.whitespace && filters.whitespace.length > 0 ? filters.whitespace : undefined,
          viewingMode: filters.viewingMode,
          penetrationMin: filters.penetrationMin,
          penetrationMax: filters.penetrationMax,
          crimeIndex: filters.crimeIndex,
          weatherHotMonthsMin: filters.weatherHotMonthsMin,
          weatherHotMonthsMax: filters.weatherHotMonthsMax,
          weatherColdMonthsMin: filters.weatherColdMonthsMin,
          weatherColdMonthsMax: filters.weatherColdMonthsMax,
          shoppingComplexOnly: filters.shoppingComplexOnly,
          cartOrdinance: filters.cartOrdinance,
        } : undefined
      );
      setStateAggregates(data);
    } finally {
      setIsLoading(false);
    }
  }, [filters.states, filters.cities, filters.retailers, filters.brands, filters.categories, filters.solutions, filters.whitespace, filters.viewingMode, filters.penetrationMin, filters.penetrationMax, filters.crimeIndex, filters.weatherHotMonthsMin, filters.weatherHotMonthsMax, filters.weatherColdMonthsMin, filters.weatherColdMonthsMax, filters.shoppingComplexOnly, filters.cartOrdinance]);

  const fetchViewport = useCallback(async () => {
    if (!map || !enabled) return;

    const zoom = map.getZoom() ?? 4;
    const tier = getZoomTier(zoom);
    setCurrentZoomTier(tier);

    if (tier === 'state') {
      setViewportStores([]);
      prevBoundsKeyRef.current = '';
      // Fetch state aggregates lazily when entering state zoom level
      await fetchStateAggregates();
      return;
    }

    const bounds = map.getBounds();
    if (!bounds) return;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const boundsKey = `${roundBounds(sw.lat())},${roundBounds(sw.lng())},${roundBounds(ne.lat())},${roundBounds(ne.lng())}`;

    if (boundsKey === prevBoundsKeyRef.current) return;
    prevBoundsKeyRef.current = boundsKey;

    setIsLoading(true);
    try {
      // Convert state names to codes for the viewport query
      const stateCodes = filters.states?.map((name) => STATE_NAME_TO_CODE[name.toLowerCase()]).filter(Boolean);
      const stores = await fetchStoresForViewport(
        { minLat: sw.lat(), maxLat: ne.lat(), minLng: sw.lng(), maxLng: ne.lng() },
        {
          channels: filters.channels,
          customerType: filters.customerType,
          states: stateCodes && stateCodes.length > 0 ? stateCodes : undefined,
          cities: filters.cities && filters.cities.length > 0 ? filters.cities : undefined,
          retailers: filters.retailers && filters.retailers.length > 0 ? filters.retailers : undefined,
          brands: filters.brands && filters.brands.length > 0 ? filters.brands : undefined,
          categories: filters.categories && filters.categories.length > 0 ? filters.categories : undefined,
          solutions: filters.solutions && filters.solutions.length > 0 ? filters.solutions : undefined,
          whitespace: filters.whitespace && filters.whitespace.length > 0 ? filters.whitespace : undefined,
          viewingMode: filters.viewingMode,
          penetrationMin: filters.penetrationMin,
          penetrationMax: filters.penetrationMax,
          crimeIndex: filters.crimeIndex,
          weatherHotMonthsMin: filters.weatherHotMonthsMin,
          weatherHotMonthsMax: filters.weatherHotMonthsMax,
          weatherColdMonthsMin: filters.weatherColdMonthsMin,
          weatherColdMonthsMax: filters.weatherColdMonthsMax,
          shoppingComplexOnly: filters.shoppingComplexOnly,
          cartOrdinance: filters.cartOrdinance,
        },
        tier === 'detail'
      );
      setViewportStores(stores);
    } finally {
      setIsLoading(false);
    }
  }, [map, enabled, filters.channels, filters.customerType, filters.states, filters.cities, filters.retailers, filters.brands, filters.categories, filters.solutions, filters.whitespace, filters.viewingMode, filters.penetrationMin, filters.penetrationMax, filters.crimeIndex, filters.weatherHotMonthsMin, filters.weatherHotMonthsMax, filters.weatherColdMonthsMin, filters.weatherColdMonthsMax, filters.shoppingComplexOnly, filters.cartOrdinance, fetchStateAggregates]);

  useEffect(() => {
    if (!map || !enabled) return;

    const handleIdle = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(fetchViewport, 500);
    };

    const listener = map.addListener('idle', handleIdle);
    handleIdle();

    return () => {
      listener.remove();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [map, enabled, fetchViewport]);

  useEffect(() => {
    // Reset loaded flag when disabled or filters change
    if (!enabled) {
      stateAggregatesLoadedRef.current = false;
      setStateAggregates([]);
      setViewportStores([]);
    } else {
      stateAggregatesLoadedRef.current = false;
      setStateAggregates([]);
      setIsLoading(true);
    }
    prevBoundsKeyRef.current = '';
  }, [enabled, filters.channels, filters.customerType, filters.states, filters.cities, filters.retailers, filters.brands, filters.categories, filters.solutions, filters.whitespace, filters.viewingMode, filters.penetrationMin, filters.penetrationMax, filters.crimeIndex, filters.weatherHotMonthsMin, filters.weatherHotMonthsMax, filters.weatherColdMonthsMin, filters.weatherColdMonthsMax, filters.shoppingComplexOnly, filters.cartOrdinance]);

  // Filter state aggregates by selected states (if any)
  const filteredStateAggregates = useMemo(() => {
    if (!filters.states || filters.states.length === 0) return stateAggregates;
    const selectedCodes = new Set(
      filters.states.map((name) => STATE_NAME_TO_CODE[name.toLowerCase()]).filter(Boolean)
    );
    return stateAggregates.filter((s) => selectedCodes.has(s.state_code));
  }, [stateAggregates, filters.states]);

  return { stateAggregates: filteredStateAggregates, viewportStores, isLoading, currentZoomTier };
};
